import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PauseCircle, CheckCircle2, Loader2, Wrench } from "lucide-react";
import { Section } from "@/mockups/dc";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/command-center/suspensions`;
const ago = (iso) => {
  if (!iso) return "—";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
};

function SuspendedRow({ s, onDone }) {
  const [busy, setBusy] = useState(null);
  const id = `${s.job_key}-${s.location_id}`;
  const run = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await axios.post(`${API}/${kind}`, { location_id: s.location_id, job_key: s.job_key });
      if (kind === "verify" && !data.ok) toast.error(`Still not fixed: ${data.detail || "check failed"}`);
      else toast.success(kind === "verify" ? `Fixed — resumed ${data.resumed} task${data.resumed === 1 ? "" : "s"} at ${s.location_name}` : `Resumed ${s.label}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div style={{ border: "1px solid var(--line)", borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 8 }} data-testid={`suspended-row-${id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <PauseCircle size={16} style={{ color: "var(--neg, #c2410c)" }} />
        <b>{s.location_name}</b><span className="muted">·</span><span>{s.label}</span>
        <span className="badge neg" data-testid={`suspended-failures-${id}`}>{s.consecutive_failures} failures in a row</span>
        <span className="muted" style={{ fontSize: 12, marginLeft: "auto" }}>Suspended {ago(s.suspended_at)} · last success {ago(s.last_success_at)}</span>
      </div>
      {s.last_error && <div className="muted" style={{ fontSize: 12, fontFamily: "var(--mono, monospace)", wordBreak: "break-word" }} data-testid={`suspended-error-${id}`}>Last error: {s.last_error}</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "var(--soft, #f5f9f9)", borderRadius: 8, padding: "8px 10px", fontSize: 13 }} data-testid={`suspended-fix-${id}`}>
        <Wrench size={14} style={{ marginTop: 2, flexShrink: 0 }} /><span><b>How to fix:</b> {s.suggestion}</span>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button className="btn btn-sm btn-primary" disabled={!!busy} onClick={() => run("verify")} data-testid={`suspended-verify-${id}`}>
          {busy === "verify" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} I fixed it — check now
        </button>
        <button className="btn btn-sm" disabled={!!busy} onClick={() => run("resume")} data-testid={`suspended-resume-${id}`}>
          {busy === "resume" && <Loader2 size={14} className="animate-spin" />} Resume without checking
        </button>
      </div>
    </div>
  );
}

export const ActionRequiredTab = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => axios.get(API).then((r) => setD(r.data)).catch(() => setD({ items: [], count: 0 })), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="skeleton" style={{ height: 200, borderRadius: 12 }} />;
  return (
    <Section title={`Suspended tasks (${d.count})`} icon={PauseCircle} testid="action-required"
      sub="Background tasks that failed 5 times in a row for a location are paused so they stop using the server. Fix the cause, then confirm here to restart them.">
      {d.items.length === 0 ? (
        <div className="muted" style={{ padding: "12px 0" }} data-testid="action-required-empty">Nothing needs your attention — every task is running.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {d.items.map((s) => <SuspendedRow key={`${s.job_key}-${s.location_id}`} s={s} onDone={load} />)}
        </div>
      )}
    </Section>
  );
};
